import React, { useState, useCallback } from "react";
import { StyleSheet } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { useThemeColor } from "@/hooks/useThemeColor";

import { useFirebase } from "@components/firebase/useFirebase";
import { getAllPlaces } from "@/services/firebase/place-functions";
import { PlaceModel, DateParser } from "common247";
import { PlaceCard } from "@/components/places/PlaceCard";
import { formatFavouriteRatingGroup } from "@/components/media/MediaGallery";

const PLACE_SORT_OPTIONS: ISortOption<PlaceModel>[] = [
  { key: "name", label: "Name", getValue: (p) => p.name || "" },
  { key: "favouriteRating", label: "Favourite Rating", getValue: (p) => p.favouriteRating ?? 3 },
  { key: "createdAt", label: "Created Date", getValue: (p) => p.createdAt || 0 },
  { key: "address", label: "Address / Location", getValue: (p) => p.address || p.location?.text || "" },
  { key: "owner", label: "Owner", getValue: (p) => p.ownerDisplayName || "" },
];

const PLACE_GROUP_OPTIONS: IGroupOption<PlaceModel>[] = [
  {
    key: "favouriteRating",
    label: "Favourite Rating",
    getGroupValue: (p) => formatFavouriteRatingGroup(p.favouriteRating),
  },
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (p) => p.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "year",
    label: "Year",
    getGroupValue: (p) => {
      if (p.date) {
        const parsed = DateParser.parse(p.date);
        if (parsed.from?.year) return String(parsed.from.year);
      }
      return p.createdAt ? String(new Date(p.createdAt).getFullYear()) : "No Date";
    },
  },
  {
    key: "address",
    label: "Address",
    getGroupValue: (p) => p.address || "No Address Specified",
  },
];

/**
 * Administrative screen displaying all platform places with view, sort, group, and search capabilities.
 */
export default function PlaceAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [places, setPlaces] = useState<PlaceModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (!services) return;
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      try {
        const response = await getAllPlaces(services, {});
        if (response.status) {
          setPlaces(response.places || []);
        } else {
          CustomAlert.alert("Error", response.error || "Failed to fetch places.");
        }
      } catch (error) {
        console.error("Error fetching places:", error);
        CustomAlert.alert("Error", "An unexpected error occurred while fetching places.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [services]
  );

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  return (
    <ThemedScreen title="All Places" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${places.length} TOTAL ${places.length === 1 ? "PLACE" : "PLACES"}`}
        </ThemedText>

        <ThemedList<PlaceModel>
          data={places}
          sortOptions={PLACE_SORT_OPTIONS}
          defaultSortKey="createdAt"
          defaultSortDirection="desc"
          groupOptions={PLACE_GROUP_OPTIONS}
          defaultGroupKey="owner"
          persistenceKey="admin-all-places"
          pageSize={24}
          filterKeys={["name", "description", "address", "ownerDisplayName"]}
          filterPlaceholder="Search all places..."
          loading={loading}
          onRefresh={() => fetchData(true)}
          refreshing={refreshing}
          emptyText="No places found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, _isHovered, viewMode) => (
            <PlaceCard
              place={item}
              showOwner={true}
              isAdminView={true}
              canEditTags={false}
              viewMode={viewMode}
              onPress={() => {}}
            />
          )}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 24,
    gap: 12,
  },
});
